import { useCallback, useRef, useState, useEffect, useMemo } from "react";
import { RcFile } from "antd/lib/upload/interface";
import styles from "../ChatButton.module.less";
import { useChatContext } from "../../../contexts/ChatContext";
import { ChatStep } from "../../../types/chat";
import RenderComponent from "./renderComponents/RenderComponent";
import ChatInput from "./renderComponents/ChatInput";
import { UserOutlined } from "@ant-design/icons";

const ChatWindow = () => {
  const {
    state,
    toggleChat,
    selectStep,
    goBack,
    getCurrentOptions,
    getCurrentStep,
    handleSubmit,
    handleCancelCurrentStep,
  } = useChatContext();

  const [attachments, setAttachments] = useState<RcFile[]>([]);
  const [currentOptions, setCurrentOptions] = useState<ChatStep[]>([]);
  const [inputDisabled, setInputDisabled] = useState(false);
  const messageEndRef = useRef<HTMLDivElement>(null);
  const chatBodyRef = useRef<HTMLDivElement>(null);

  // Load options asynchronously when needed
  useEffect(() => {
    setInputDisabled(false);

    const loadOptions = async () => {
      try {
        const options = await getCurrentOptions();
        setCurrentOptions(options);
      } catch (error) {
        setCurrentOptions([]);
      }
    };

    loadOptions();
  }, [getCurrentOptions, state.selectedSteps]);

  const handleCloseChat = useCallback(async () => {
    await handleCancelCurrentStep();
    toggleChat();
  }, [handleCancelCurrentStep, toggleChat]);

  const handleOptionSelect = useCallback(
    (step: ChatStep) => {
      selectStep(step);
      setAttachments([]);

      // If the selected step has input config enabled, keep input enabled
      // Otherwise disable it (for direct submit steps)
      if (step.inputConfig?.enabled) {
        setInputDisabled(false);
      } else {
        setInputDisabled(true);
      }
    },
    [selectStep],
  );

  const handleAttachmentsChange = useCallback((files: RcFile[]) => {
    setAttachments(files);
  }, []);

  const handleSend = useCallback(
    (text: string, files: RcFile[]) => {
      if (text.trim()) {
        handleSubmit(text, files);
        setAttachments([]);
        setInputDisabled(true);
      }
    },
    [handleSubmit],
  );

  const scrollToBottom = useCallback(() => {
    if (messageEndRef.current) {
      messageEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [state.history, scrollToBottom]);

  // Check if input should be visible
  const isInputVisible = useMemo(() => {
    const currentStep = getCurrentStep();

    // First check if current step has input config enabled
    if (currentStep?.inputConfig?.enabled && !inputDisabled) {
      return true;
    }

    // If we have current options and the last selected step matches one of them,
    // check if that option has input config enabled
    if (state.selectedSteps.length > 0 && currentOptions.length > 0) {
      const lastSelectedStep =
        state.selectedSteps[state.selectedSteps.length - 1];
      const matchingOption = currentOptions.find(
        (option) => option.stepId === lastSelectedStep.stepId,
      );

      if (matchingOption?.inputConfig?.enabled && !inputDisabled) {
        return true;
      }
    }

    return false;
  }, [getCurrentStep, inputDisabled, state.selectedSteps, currentOptions]);

  // Get input configuration from current step or matching option
  const inputConfig = useMemo(() => {
    const currentStep = getCurrentStep();

    // First try to get from current step
    if (currentStep?.inputConfig) {
      return currentStep.inputConfig;
    }

    // If not found, try to get from current options
    if (state.selectedSteps.length > 0 && currentOptions.length > 0) {
      const lastSelectedStep =
        state.selectedSteps[state.selectedSteps.length - 1];
      const matchingOption = currentOptions.find(
        (option) => option.stepId === lastSelectedStep.stepId,
      );

      if (matchingOption?.inputConfig) {
        return matchingOption.inputConfig;
      }
    }

    return undefined;
  }, [getCurrentStep, state.selectedSteps, currentOptions]);

  const { isTyping } = state;

  // Format message text with proper line breaks for better readability
  const formatMessageText = (text: string) => {
    if (!text) return "";
    return text.split("\n").map((line, i) => (
      <span key={i}>
        {line}
        {i < text.split("\n").length - 1 && <br />}
      </span>
    ));
  };

  // Check if back button should be shown
  const shouldShowBackButton = useMemo(() => {
    if (state.selectedSteps.length === 0) {
      return false;
    }

    const currentStep = getCurrentStep();

    // If showBackButton is explicitly set to false, don't show it
    if (currentStep?.showBackButton === false) {
      return false;
    }

    // Default behavior: show back button if there are selected steps
    return state.selectedSteps.length > 0;
  }, [getCurrentStep, state.selectedSteps]);

  return (
    <div className={styles.chatWindow}>
      <div className={styles.chatHeader}>
        <span className={styles.chatTitle}>Chat</span>
        <button
          onClick={handleCloseChat}
          style={{
            background: "none",
            border: "none",
            color: "#333",
            cursor: "pointer",
            fontSize: "22px",
          }}
        >
          &times;
        </button>
      </div>

      <div className={styles.chatWindowContent}>
        {/* Chat messages */}
        <div className={styles.chatBody} ref={chatBodyRef}>
          {/* Message list */}
          {state.history.map((message, index) =>
            message.type === "mike" ? (
              <div key={index} className={styles.mikeMessage}>
                <div className={styles.mikeAvatar}>
                  <img
                    src="/images/mike.opac.png"
                    alt="Mike"
                    width="24"
                    height="24"
                  />
                </div>
                <div className={styles.mikeMessageContent}>
                  {formatMessageText(message.text)}

                  {/* Render components */}
                  {message.renderObjects &&
                    message.renderObjects.length > 0 && (
                      <div className={styles.renderObjectsContainer}>
                        {message.renderObjects.map((renderObject, rIndex) => (
                          <RenderComponent
                            key={`render-${index}-${rIndex}`}
                            renderObject={renderObject}
                          />
                        ))}
                      </div>
                    )}
                </div>
              </div>
            ) : (
              <div key={index} className={styles.userMessage}>
                <div className={styles.userAvatar}>
                  <UserOutlined />
                </div>
                <div className={styles.userMessageContent}>
                  {formatMessageText(message.text)}
                  {message.attachments && message.attachments.length > 0 && (
                    <div className={styles.messageAttachments}>
                      <p className={styles.attachmentsTitle}>Attachments:</p>
                      {message.attachments.map((attachment, aIndex) => (
                        <span
                          key={`attachment-${index}-${aIndex}`}
                          className={styles.attachmentBadge}
                        >
                          {attachment.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ),
          )}

          {/* Typing indicator */}
          {isTyping && (
            <div className={styles.mikeMessage}>
              <div className={styles.mikeAvatar}>
                <img
                  src="/images/mike.opac.png"
                  alt="Mike"
                  width="24"
                  height="24"
                />
              </div>
              <div className={styles.typingIndicator}>
                <span className={styles.typingDot}></span>
                <span className={styles.typingDot}></span>
                <span className={styles.typingDot}></span>
              </div>
            </div>
          )}

          {/* Options list */}
          {state.history.length > 0 &&
            state.history[state.history.length - 1].type === "mike" &&
            currentOptions.length > 0 &&
            !isTyping && (
              <div className={styles.optionsContainer}>
                {/* Back button */}
                {shouldShowBackButton && (
                  <button
                    className={`${styles.optionButton} ${styles.backButton}`}
                    onClick={goBack}
                  >
                    ← Back
                  </button>
                )}

                {/* Options */}
                {currentOptions.map((option, index) => (
                  <button
                    key={`option-${option.stepId}-${index}`}
                    className={styles.optionButton}
                    onClick={() => handleOptionSelect(option)}
                  >
                    {option.title || String(option.stepId)}
                  </button>
                ))}
              </div>
            )}

          {/* Message end reference */}
          <div ref={messageEndRef} />
        </div>

        {/* Footer */}
        <p className={styles.footerText}>We are here to serve you better</p>

        {/* Unified input field */}
        {isInputVisible && (
          <div className={styles.inputContainer}>
            <ChatInput
              onAttachmentsChange={handleAttachmentsChange}
              attachmentFiles={attachments}
              onSend={handleSend}
              isActiveAttachment={inputConfig?.attachments ?? false}
              placeholder={inputConfig?.placeholder}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatWindow;
